/**
 * /admin route layout — guard + shell.
 *
 * Story 73 — System Admin User is redirected to `/admin` after sign-in,
 * non-admin Users land on `/dashboard`. This layout enforces the same
 * invariant at the route level: it reads `/api/admin/whoami` (fresh per
 * request — invariant 6) and redirects unauthenticated users to /sign-in
 * and non-admin users to /dashboard.
 *
 * Children pages render inside `Shell mode="admin"` which owns the sidebar nav.
 */

import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, type WhoamiPayload } from '../../state/admin/api.ts';
import { Shell } from '../../components/shell/Shell.tsx';
import { Heading, Eyebrow } from '@/components/ui';
import '../../components/admin/admin-shell.css';

export interface AdminLayoutProps {
  children: ReactNode;
  title?: string;
  eyebrow?: string;
}

type WhoamiState = 'loading' | 'denied' | WhoamiPayload;

export function AdminLayout({
  children,
  title,
  eyebrow,
}: AdminLayoutProps): ReactElement {
  const navigate = useNavigate();
  const [state, setState] = useState<WhoamiState>('loading');

  useEffect(() => {
    void (async () => {
      const res = await adminApi.whoami();
      if (!res.ok) {
        setState('denied');
        return;
      }
      setState(res.data);
    })();
  }, []);

  useEffect(() => {
    if (state === 'denied') {
      navigate('/sign-in');
      return;
    }
    if (typeof state !== 'string' && !state.is_system_admin) {
      navigate('/dashboard');
    }
  }, [state, navigate]);

  if (state === 'loading' || state === 'denied') {
    return (
      <main className="admin-shell">
        <p className="admin-shell-loading">loading…</p>
      </main>
    );
  }
  if (!state.is_system_admin) {
    // Brief render while the redirect effect runs.
    return (
      <main className="admin-shell">
        <p className="admin-shell-loading">redirecting…</p>
      </main>
    );
  }

  return (
    <Shell mode="admin" densityMode="workspace">
      {(eyebrow ?? title) ? (
        <header className="admin-shell-header">
          {/* 15-FG/24: migrated from admin-shell-eyebrow/admin-shell-title hard-coded CSS
              to Eyebrow + Heading primitives (audit-27 director-signal #6: admin pages
              bypassed primitives via hard-coded 22px/11px CSS). Three modes now share
              the same typography substrate. */}
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          {title ? <Heading level="h1" className="mt-2">{title}</Heading> : null}
        </header>
      ) : null}
      {children}
    </Shell>
  );
}
